import { useState, useEffect } from "react";
import Table from "components/DesignSystem/Table/V2/Table";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import BarePriceInput from "components/PriceInput/BarePriceInput";
import Card from "components/DesignSystem/Card/Card";
import { HelperText } from "components/DesignSystem/HelperText/HelperText";
import { getSeasonsFrom } from "utils/getAllSeason";
import dayjs from "dayjs";
import { useToast } from "hooks/useToast";
import {
  useGetEstimatedTaxSettingsQuery,
  useUpdateEstimatedTaxSettingsMutation,
  useGetS174EntriesQuery,
  useDeleteS174EntryMutation,
} from "store/apis/estimatedTax";
import { S174Entry } from "types/Models/estimatedTax";
import { BackendError } from "types/utils/error";
import { EditPencil } from "components/icons/EditPencil";
import { DeleteIcon } from "components/icons/delete";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from "react-table-8.10.7";
import { AddS174EntryModal } from "./TaxBenefits/AddS174EntryModal";

const createS174Column = createColumnHelper<S174Entry>();

const currentYear = dayjs().year();
const NOL_YEAR_OPTIONS = getSeasonsFrom(2009, currentYear + 1)
  .reverse()
  .map((y) => ({ value: String(y), label: String(y) }));
const OVERPAYMENT_YEAR_OPTIONS = getSeasonsFrom(currentYear - 5, currentYear)
  .reverse()
  .map((y) => ({ value: String(y), label: String(y) }));

type Props = {
  entityId: string;
};

type NolForm = {
  nol_balance: string;
  nol_period_year: string;
};

type OverpaymentForm = {
  prior_year_overpayment: string;
  overpayment_year: string;
};

export const EstimatedTaxSettingsPanel = ({ entityId }: Props) => {
  const { alertToast, successToast } = useToast();

  // NOL edit state
  const [editingNol, setEditingNol] = useState(false);
  const [nolForm, setNolForm] = useState<NolForm>({
    nol_balance: "",
    nol_period_year: "",
  });

  // Prior year overpayment edit state
  const [editingOverpayment, setEditingOverpayment] = useState(false);
  const [overpaymentForm, setOverpaymentForm] = useState<OverpaymentForm>({
    prior_year_overpayment: "",
    overpayment_year: "",
  });
  const [overpaymentYearError, setOverpaymentYearError] = useState<
    string | null
  >(null);

  // §174 modal state
  const [s174Modal, setS174Modal] = useState<{
    open: boolean;
    entry: S174Entry | null;
  }>({ open: false, entry: null });

  const { data: settings, isLoading: isLoadingSettings } =
    useGetEstimatedTaxSettingsQuery({ entityId }, { skip: !entityId });

  const { data: s174Entries = [], isLoading: isLoadingS174 } =
    useGetS174EntriesQuery({ entityId }, { skip: !entityId });

  useEffect(() => {
    if (settings) {
      setNolForm({
        nol_balance: settings.nol_balance ? String(settings.nol_balance) : "",
        nol_period_year: settings.nol_period_year
          ? String(settings.nol_period_year)
          : "",
      });
      setOverpaymentForm({
        prior_year_overpayment: settings.prior_year_overpayment
          ? String(settings.prior_year_overpayment)
          : "",
        overpayment_year: settings.overpayment_year
          ? String(settings.overpayment_year)
          : "",
      });
    }
  }, [settings]);

  const [updateSettings, { isLoading: isSavingNol }] =
    useUpdateEstimatedTaxSettingsMutation();
  const [deleteEntry] = useDeleteS174EntryMutation();

  const buildSettingsPayload = () => {
    const nolBalance = Number(nolForm.nol_balance) || 0;
    const overpaymentAmount =
      Number(overpaymentForm.prior_year_overpayment) || 0;

    return {
      nol_balance: nolBalance,
      nol_period_year:
        nolBalance > 0 && nolForm.nol_period_year
          ? Number(nolForm.nol_period_year)
          : null,
      prior_year_overpayment: overpaymentAmount,
      overpayment_year:
        overpaymentAmount > 0 && overpaymentForm.overpayment_year
          ? Number(overpaymentForm.overpayment_year)
          : null,
    };
  };

  const handleSaveNol = async () => {
    try {
      await updateSettings({
        entityId,
        payload: {
          ...buildSettingsPayload(),
          setup_completed: true,
        },
      }).unwrap();
      successToast({ message: "NOL settings saved" });
      setEditingNol(false);
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to save NOL settings",
      });
    }
  };

  const handleSaveOverpayment = async () => {
    const amount = Number(overpaymentForm.prior_year_overpayment) || 0;
    if (amount > 0 && !overpaymentForm.overpayment_year) {
      setOverpaymentYearError(
        "Overpayment year is required when amount is set."
      );
      return;
    }
    setOverpaymentYearError(null);

    try {
      await updateSettings({
        entityId,
        payload: {
          ...buildSettingsPayload(),
          setup_completed: settings?.setup_completed,
        },
      }).unwrap();
      successToast({ message: "Prior year overpayment saved" });
      setEditingOverpayment(false);
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to save overpayment",
      });
    }
  };

  const openAddS174 = () => {
    setS174Modal({ open: true, entry: null });
  };

  const openEditS174 = (entry: S174Entry) => {
    setS174Modal({ open: true, entry });
  };

  const handleOverpaymentYearChange = (option: unknown) => {
    const selected = option as OptionData | null;
    setOverpaymentForm({
      ...overpaymentForm,
      overpayment_year: selected ? selected.value : "",
    });
    if (selected) setOverpaymentYearError(null);
  };

  const handleCancelOverpayment = () => {
    setEditingOverpayment(false);
    setOverpaymentYearError(null);
  };

  const handleDeleteS174 = async (entryId: string) => {
    try {
      await deleteEntry({ entityId, entryId }).unwrap();
      successToast({ message: "§174 entry removed" });
    } catch {
      alertToast({ message: "Failed to remove §174 entry" });
    }
  };

  const s174Columns = [
    createS174Column.accessor("period_year", {
      header: "Year",
      cell: (info) => (
        <span className="t-font-medium t-text-text-100">{info.getValue()}</span>
      ),
    }),
    createS174Column.accessor("amount", {
      header: "Amount Remaining",
      cell: (info) => (
        <span className="t-font-medium t-text-text-100">
          ${info.getValue().toLocaleString()}
        </span>
      ),
    }),
    createS174Column.accessor("monthly_deduction", {
      header: "Monthly Deduction",
      cell: (info) => (
        <div className="t-flex t-items-center t-justify-between">
          <span className="t-font-medium t-text-text-100">
            $
            {info.getValue().toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <div className="t-flex">
            <ToolTip text="Edit entry">
              <Button
                customType="ghost"
                size="small"
                onClick={() => openEditS174(info.row.original)}
              >
                <EditPencil className="t-text-text-30" />
              </Button>
            </ToolTip>
            <ToolTip text="Delete entry">
              <Button
                customType="ghost"
                size="small"
                onClick={() => handleDeleteS174(info.row.original.id)}
              >
                <span className="t-text-text-30">
                  <DeleteIcon color="currentColor" />
                </span>
              </Button>
            </ToolTip>
          </div>
        </div>
      ),
    }),
  ];

  const s174Table = useReactTable({
    data: s174Entries,
    columns: s174Columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoadingSettings || isLoadingS174) return null;

  return (
    <div className="t-space-y-6">
      {/* Prior Year Overpayment */}
      <Card.Root>
        <Card.Header bordered={false}>
          <div className="t-flex t-items-center t-justify-between t-w-full">
            <div>
              <Card.Title>Prior Year Overpayment</Card.Title>
              <p className="t-text-body-sm t-text-text-30 t-mt-0.5">
                Overpayment from last year applied as a credit against this
                year's estimated tax.
              </p>
            </div>
            {!editingOverpayment && (
              <Button size="small" onClick={() => setEditingOverpayment(true)}>
                {settings?.prior_year_overpayment ? "Edit" : "+ Set Up"}
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="t-mt-2">
          {editingOverpayment ? (
            <div className="t-space-y-4">
              <div className="t-grid t-grid-cols-2 t-gap-4">
                <Combobox
                  label="Overpayment Year"
                  options={OVERPAYMENT_YEAR_OPTIONS}
                  value={
                    OVERPAYMENT_YEAR_OPTIONS.find(
                      (o) => o.value === overpaymentForm.overpayment_year
                    ) || null
                  }
                  onChange={handleOverpaymentYearChange}
                  isClearable
                  placeholder="Select year"
                  menuPortalTarget={document.body}
                />
                <BarePriceInput
                  label="Overpayment Amount"
                  value={overpaymentForm.prior_year_overpayment}
                  onChange={(e) =>
                    setOverpaymentForm({
                      ...overpaymentForm,
                      prior_year_overpayment: e.target.value,
                    })
                  }
                />
              </div>
              {overpaymentYearError && (
                <HelperText variant="error">{overpaymentYearError}</HelperText>
              )}
              <div className="t-flex t-gap-2 t-justify-end">
                <Button
                  disabled={isSavingNol}
                  onClick={handleCancelOverpayment}
                >
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  isLoading={isSavingNol}
                  disabled={isSavingNol}
                  onClick={handleSaveOverpayment}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : settings?.prior_year_overpayment ? (
            <div className="t-rounded-lg t-border t-border-neutral-10 t-overflow-hidden">
              <div className="t-grid t-grid-cols-2 t-bg-surface-lighter-grey t-px-4 t-py-2 t-text-body-sm t-font-medium t-text-text-30 t-border-b t-border-neutral-10">
                <span>Year</span>
                <span>Overpayment Amount</span>
              </div>
              <div className="t-grid t-grid-cols-2 t-px-4 t-py-3 t-text-body-sm">
                <span className="t-font-medium t-text-text-100">
                  {settings.overpayment_year || "—"}
                </span>
                <span className="t-font-medium t-text-text-100">
                  ${settings.prior_year_overpayment.toLocaleString()}
                </span>
              </div>
            </div>
          ) : (
            <p className="t-text-body-sm t-text-text-30">
              No prior year overpayment configured.
            </p>
          )}
        </Card.Body>
      </Card.Root>

      {/* NOL Settings */}
      <Card.Root>
        <Card.Header bordered={false}>
          <div className="t-flex t-items-center t-justify-between t-w-full">
            <div>
              <Card.Title>Prior Year Loss Credit</Card.Title>
              <p className="t-text-body-sm t-text-text-30 t-mt-0.5">
                Loss carryforward balance applied against taxable income (capped
                at 80% per year).
              </p>
            </div>
            {!editingNol && (
              <Button size="small" onClick={() => setEditingNol(true)}>
                {settings?.setup_completed ? "Edit" : "+ Set Up"}
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="t-mt-2">
          {editingNol ? (
            <div className="t-space-y-4">
              <div className="t-grid t-grid-cols-2 t-gap-4">
                <BarePriceInput
                  label="Loss Credit Balance"
                  value={nolForm.nol_balance}
                  onChange={(e) =>
                    setNolForm({ ...nolForm, nol_balance: e.target.value })
                  }
                />
                <Combobox
                  label="Loss Period Year"
                  options={NOL_YEAR_OPTIONS}
                  value={
                    NOL_YEAR_OPTIONS.find(
                      (o) => o.value === nolForm.nol_period_year
                    ) || null
                  }
                  onChange={(option) => {
                    const selected = option as OptionData | null;
                    setNolForm({
                      ...nolForm,
                      nol_period_year: selected ? selected.value : "",
                    });
                  }}
                  isClearable={false}
                  placeholder="Select year"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="t-flex t-gap-2 t-justify-end">
                <Button
                  disabled={isSavingNol}
                  onClick={() => setEditingNol(false)}
                >
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  isLoading={isSavingNol}
                  disabled={isSavingNol}
                  onClick={handleSaveNol}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : settings?.setup_completed && settings.nol_balance > 0 ? (
            <div className="t-rounded-lg t-border t-border-neutral-10 t-overflow-hidden">
              <div className="t-grid t-grid-cols-2 t-bg-surface-lighter-grey t-px-4 t-py-2 t-text-body-sm t-font-medium t-text-text-30 t-border-b t-border-neutral-10">
                <span>Loss Credit Balance</span>
                <span>Period Year</span>
              </div>
              <div className="t-grid t-grid-cols-2 t-px-4 t-py-3 t-text-body-sm">
                <span className="t-font-medium t-text-text-100">
                  ${settings.nol_balance.toLocaleString()}
                </span>
                <span className="t-font-medium t-text-text-100">
                  {settings.nol_period_year || "—"}
                </span>
              </div>
            </div>
          ) : (
            <p className="t-text-body-sm t-text-text-30">
              No prior year loss credit configured.
            </p>
          )}
        </Card.Body>
      </Card.Root>

      {/* §174 Settings */}
      <Card.Root>
        <Card.Header bordered={false}>
          <div className="t-flex t-items-center t-justify-between t-w-full">
            <div>
              <Card.Title>§174 R&D Amortisation</Card.Title>
              <p className="t-text-body-sm t-text-text-30 t-mt-0.5">
                R&D expenses amortised over a 15-year schedule. Each tranche is
                tracked separately.
              </p>
            </div>
            <Button size="small" onClick={openAddS174}>
              + Add Entry
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="t-mt-2">
          {s174Entries.length > 0 ? (
            <Table.Container
              sticky={false}
              className="t-w-full t-overflow-x-auto"
            >
              <Table.Content className="t-text-body-sm">
                <Table.Head>
                  {s174Table.getHeaderGroups().map((headerGroup) => (
                    <Table.HeadRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeadCell key={header.id}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Table.HeadCell>
                      ))}
                    </Table.HeadRow>
                  ))}
                </Table.Head>
                <Table.Body>
                  {s174Table.getRowModel().rows.map((row) => (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                  {s174Entries.length > 1 && (
                    <Table.Row className="t-bg-surface-lighter-grey">
                      <Table.Cell
                        colSpan={3}
                        className="t-font-semibold t-text-text-60"
                      >
                        Total monthly deduction
                      </Table.Cell>
                      <Table.Cell className="t-font-semibold t-text-text-100">
                        $
                        {(
                          settings?.s174_total_monthly_deduction ?? 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table.Content>
            </Table.Container>
          ) : (
            <p className="t-text-body-sm t-text-text-30">
              No §174 amortisation entries configured.
            </p>
          )}
        </Card.Body>
      </Card.Root>

      {s174Modal.open && (
        <AddS174EntryModal
          open
          onClose={() => setS174Modal({ open: false, entry: null })}
          entityId={entityId}
          entry={s174Modal.entry}
        />
      )}
    </div>
  );
};
